'use client';

import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';
import { displayedEmail } from '@/app/(main)/client-detail/utils/contacts';
import { Modal } from '@/app/components/Modal';
import { modalActionButtonStyles } from '@/app/components/Modal/styles';
import { Tooltip } from '@/app/components/Tooltip';
import { TooltipWrapper } from '@/app/components/Tooltip/components/TooltipWrapper';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import { Input } from '@/app/components/form/Input';
import { useMediaUpload } from '@/hooks/useMediaUpload';
import { firstMediaUuid } from '@/hooks/useMediaUpload/utils';
import type { ErrorResponse } from '@/types/apiTypes';
import { setBEFieldError } from '@/utils/setBEFieldError';
import { cn } from '@/utils/сn';
import { MAX_CREDIT_CARDS_COUNT } from '../../../../consts/client-detail';
import { useChangeClientStatusMutation } from '../../../../hooks/useChangeClientStatusMutation';
import { useGetClientQuery } from '../../../../hooks/useGetClientQuery';
import { useGetCreditCardsListQuery } from '../../../../hooks/useGetCreditCardsListQuery';
import { useShareClient } from '../../../../hooks/useShareClient';
import { buildShareClientPayload } from '../../utils/buildShareClientPayload';
import { getDefaultMessageTemplateByForms, resolveMessageForSelection } from '../../utils/getShareModalTemplateMessage';
import { hasShareAttachmentError } from '../../utils/hasShareAttachmentError';
import { SharePdfDropZone } from '../SharePdfDropZone';
import { CardsGroup } from './CardsGroup';
import { StatusMessage } from './StatusMessage';
import { MessageField, SignatureSection } from './components';
import {
  LEGACY_SHARE_MODAL_DRAFT_STORAGE_KEY,
  SHARE_ATTACHMENT_UPLOAD_OPTIONS,
  type SHARE_CARD_DETAILS_FORM_TYPE,
} from './consts';
import { emailSchema, MAX_MESSAGE_LENGTH } from './emailFormSchema';
import { ModalStatusEnum } from './modalStatusEnum';
import type { EmailFormData, ShareViaEmailModalFormProps, ShareViaEmailModalProps } from './types';

const MODAL_FRAME_CLASS_NAME = 'flex flex-col w-full max-h-[min(800px,90dvh)] overflow-hidden rounded-[1.5rem]';

const INITIAL_SELECTED_FORMS: SHARE_CARD_DETAILS_FORM_TYPE[] = ['profile'];

export const ShareViaEmailModal = (props: ShareViaEmailModalProps) => {
  const {
    data: clientResponse,
    isLoading: isClientLoading,
    isFetchedAfterMount: isClientFetchedAfterMount,
  } = useGetClientQuery(undefined, {
    clientIdOverride: String(props.clientId),
  });

  if (isClientLoading && !isClientFetchedAfterMount) {
    return (
      <Modal open={props.isOpen} onClose={props.onClose}>
        <div className={MODAL_FRAME_CLASS_NAME}>
          <StatusMessage variant="sending" title="Loading…" />
        </div>
      </Modal>
    );
  }

  return <ShareViaEmailModalForm {...props} clientEmail={displayedEmail(clientResponse?.data)} />;
};

const ShareViaEmailModalForm = ({
  isOpen: isShareViaEmailModalOpen,
  onClose,
  clientId,
  clientEmail,
  shouldSetPendingAfterProfileShare = true,
  useTargetClientIdForProfilePendingStatus = false,
  onPendingStatusAfterProfileShare,
}: ShareViaEmailModalFormProps) => {
  const clientQueryOptions = { clientIdOverride: String(clientId) };

  const { mutateAsync: shareClient, isPending: isShareClientPending } = useShareClient();
  const { mutate: changeClientStatus } = useChangeClientStatusMutation();
  const { uploadMediaAuto } = useMediaUpload();
  const { data: creditCardsList } = useGetCreditCardsListQuery(clientQueryOptions);

  const [modalState, setModalState] = useState<ModalStatusEnum | null>(null);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);
  const [attachmentUploadError, setAttachmentUploadError] = useState(false);
  const [isSignatureEditing, setIsSignatureEditing] = useState(false);

  const modalStatus = modalState || ModalStatusEnum.form;

  useEffect(() => {
    try {
      window.localStorage.removeItem(LEGACY_SHARE_MODAL_DRAFT_STORAGE_KEY);
    } catch (error) {
      console.warn('Could not clear the legacy Send Forms draft from localStorage', error);
    }
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    setError,
    setValue,
    control,
  } = useForm<EmailFormData>({
    resolver: yupResolver(emailSchema),
    mode: 'onChange',
    defaultValues: {
      email: clientEmail,
      cc_email: '',
      bcc_email: '',
      selectedForms: INITIAL_SELECTED_FORMS,
      message: getDefaultMessageTemplateByForms(INITIAL_SELECTED_FORMS),
      signature: null,
    },
  });

  const selectedForms = useWatch({ control, name: 'selectedForms' });
  const messageValue = useWatch({ control, name: 'message' }) ?? '';
  const signature = useWatch({ control, name: 'signature' });

  const isCreditCardsLimitReached = useMemo(
    () => (creditCardsList?.data.length ?? 0) >= MAX_CREDIT_CARDS_COUNT,
    [creditCardsList?.data.length]
  );

  const onSubmit = async (values: EmailFormData) => {
    setModalState(ModalStatusEnum.sending);

    let attachmentMediaUuids: string[] | undefined;
    if (attachedFiles.length > 0) {
      try {
        const uploadResults = await Promise.all(
          attachedFiles.map((file) => uploadMediaAuto(file, SHARE_ATTACHMENT_UPLOAD_OPTIONS))
        );
        attachmentMediaUuids = uploadResults.map(firstMediaUuid);
      } catch {
        toast.error(`We couldn't send the email. Please try again`);
        setAttachmentUploadError(true);
        setModalState(null);
        return;
      }
    }

    try {
      await shareClient({
        clientId,
        data: buildShareClientPayload({ values, attachmentMediaUuids }),
      });

      setModalState(ModalStatusEnum.success);
      reset();
      setAttachedFiles([]);
      setAttachmentUploadError(false);

      if (shouldSetPendingAfterProfileShare && values.selectedForms.includes('profile')) {
        changeClientStatus({
          status: 'PENDING',
          ...(useTargetClientIdForProfilePendingStatus ? { targetClientId: clientId } : {}),
        });
      }
      onPendingStatusAfterProfileShare?.();
    } catch (errors) {
      const errorResponse = errors as ErrorResponse<EmailFormData> | undefined;

      toast.error(`We couldn't send the email. Please try again`);
      setBEFieldError(errorResponse as ErrorResponse<unknown>, setError);
      // Keep the form open on attachment errors so the drop zone shows them, as for signature errors
      const hasAttachmentError = hasShareAttachmentError(errorResponse as ErrorResponse<unknown>);
      if (hasAttachmentError) {
        setAttachmentUploadError(true);
      }
      setModalState(errorResponse?.errors?.signature || hasAttachmentError ? null : ModalStatusEnum.error);
    }
  };

  const handleClose = useCallback(() => {
    onClose();
    reset();
    setAttachedFiles([]);
    setAttachmentUploadError(false);
    setIsSignatureEditing(false);
  }, [reset, onClose]);

  const handleSignatureChange = useCallback(
    (nextSignature: string | null) => setValue('signature', nextSignature, { shouldValidate: true, shouldDirty: true }),
    [setValue]
  );

  const toggleForm = useCallback(
    (type: SHARE_CARD_DETAILS_FORM_TYPE) => {
      const next = selectedForms.includes(type)
        ? selectedForms.filter((formType) => formType !== type)
        : [...selectedForms, type];

      setValue('selectedForms', next, { shouldValidate: true, shouldDirty: true });
      setValue(
        'message',
        resolveMessageForSelection({ message: messageValue, previousForms: selectedForms, nextForms: next }),
        { shouldValidate: true, shouldDirty: true }
      );
    },
    [messageValue, selectedForms, setValue]
  );

  const renderFullEmailForm = () => (
    <div className="space-y-4">
      <Input
        id="clientEmail"
        label="Email"
        placeholder="Enter email address"
        autoFocus
        errors={errors}
        required
        disabled={isLoading}
        {...register('email')}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          id="cc"
          label="CC"
          placeholder="Enter CC"
          errors={errors}
          disabled={isLoading}
          {...register('cc_email')}
        />
        <Input
          id="bcc"
          label="BCC"
          placeholder="Enter BCC"
          errors={errors}
          disabled={isLoading}
          {...register('bcc_email')}
        />
      </div>

      <MessageField
        id="clientMessage"
        value={messageValue}
        maxLength={MAX_MESSAGE_LENGTH}
        disabled={isLoading}
        registration={register('message')}
      >
        <SignatureSection
          clientId={clientId}
          signature={signature}
          isEditing={isSignatureEditing}
          disabled={isLoading}
          onEditingChange={setIsSignatureEditing}
          onSignatureChange={handleSignatureChange}
          errorMessage={errors.signature?.message}
        />
      </MessageField>

      <SharePdfDropZone
        files={attachedFiles}
        onChange={(files) => {
          setAttachmentUploadError(false);
          setAttachedFiles(files);
        }}
        disabled={isLoading}
        uploadError={attachmentUploadError}
      />
    </div>
  );

  const isLoading = isShareClientPending;
  const isCreditCardSendBlocked = isCreditCardsLimitReached && selectedForms.includes('credit_card');
  const isSendDisabled = !isValid || isLoading || isSignatureEditing || isCreditCardSendBlocked;

  const renderForm = () => {
    switch (modalStatus) {
      case ModalStatusEnum.sending: {
        return (
          <StatusMessage variant="sending" title="Sending…" description="Please wait while we deliver the invitation" />
        );
      }

      case ModalStatusEnum.success: {
        return (
          <StatusMessage
            variant="success"
            title="Information Shared"
            description="The client will receive an email containing the information you shared"
            action={
              <BaseButton intent="primary" onClick={handleClose} size="full">
                {'OK'}
              </BaseButton>
            }
          />
        );
      }
      case ModalStatusEnum.error: {
        return (
          <StatusMessage
            variant="error"
            title="Something Went Wrong"
            description="We couldn’t send the email. Please try again"
            action={
              <BaseButton intent="primary" onClick={handleClose} size="full">
                {'OK'}
              </BaseButton>
            }
          />
        );
      }

      case ModalStatusEnum.form: {
        return (
          <div className="flex flex-col w-full md:w-[550px] max-h-[min(800px,90dvh)]">
            <div className="px-4 md:px-[36px] pt-6 md:pt-[46px]">
              <h2 className="light text-blue-900 mb-[6px] text-center">Share</h2>
              <p className="md:text-body-regular text-body-small text-grey-700 mb-6 md:mb-[34px]">
                Send the client profile, credit card details, or both in one secure step.
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col min-h-0 overflow-hidden">
              <div className="overflow-y-auto px-4 md:px-[36px] pb-[24px]">
                <div className="mb-6">
                  <CardsGroup
                    selectedForms={selectedForms}
                    onToggle={toggleForm}
                    isCreditCardsLimitReached={isCreditCardsLimitReached}
                    isLoading={isLoading}
                    isSelectionInvalid={Boolean(errors.selectedForms)}
                    selectionErrorMessage={errors.selectedForms?.message}
                  />
                </div>

                {renderFullEmailForm()}
              </div>

              <div className="flex gap-4 md:gap-[20px] w-full px-4 md:px-[36px] py-4 md:py-[24px] border-t border-grey-100 shrink-0">
                <BaseButton
                  intent="secondary"
                  type="button"
                  onClick={handleClose}
                  size="full"
                  className={cn('flex-1', modalActionButtonStyles())}
                >
                  Cancel
                </BaseButton>
                <div className="flex-1 md:flex-none">
                  <Tooltip
                    placement="top"
                    isVisible={isCreditCardSendBlocked}
                    content={
                      <TooltipWrapper>
                        You&apos;ve reached the {MAX_CREDIT_CARDS_COUNT}-cards <br /> limit. Remove a card to send a{' '}
                        <br /> new form.
                      </TooltipWrapper>
                    }
                  >
                    <div className="md:min-w-[230px]">
                      <BaseButton
                        intent="primary"
                        className={modalActionButtonStyles()}
                        type="submit"
                        size="full"
                        disabled={isSendDisabled}
                      >
                        {'Send form'}
                      </BaseButton>
                    </div>
                  </Tooltip>
                </div>
              </div>
            </form>
          </div>
        );
      }
    }
  };

  return (
    <Modal open={isShareViaEmailModalOpen} onClose={handleClose}>
      <div className={MODAL_FRAME_CLASS_NAME}>{renderForm()}</div>
    </Modal>
  );
};
